import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { trackException, trackTraceException } from "services/trackInsights";
import { getOnboardingInformation } from "services/home";

const HomeContext = React.createContext({
  loading: true,
  setLoading: () => {},
  loadOnboarding: () => {},
  loadedOnboarding: false,
  showOnboarding: false,
  showIndicacao: false,
  milestones: [],
  setMilestones: () => {},
  milestonesError: false,
  setMilestonesError: () => {},
  isVisitaAObraConcluido: () => false,
  isVAConcluida: () => false,
  isECConcluido: () => false
});

const TRY_RETRIES_COUNT_TOTAL = 2;

export const HomeContextProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showIndicacao, setShowIndicacao] = useState(false);
  const [loadedOnboarding, setLoadedOnboarding] = useState(false);
  const [countTryOnboarding, setCountTryOnboarding] = useState(0);
  const [milestones, setMilestones] = useState([]);
  const [milestonesError, setMilestonesError] = useState(false);

  const isVAConcluida = React.useCallback(() => {
    if (!milestones?.length) return null;
    const ret = milestones.find(m => m?.id === 4);
    if (ret) {
      return ret.status === 3;
    }
    return false;
  }, [milestones]);

  const isECConcluido = React.useCallback(() => {
    if (!milestones?.length) return null;
    const ret = milestones.find(m => m?.id === 5);
    if (ret) {
      return ret.status === 3;
    }
    return false;
  }, [milestones]);

  const isVisitaAObraConcluido = React.useCallback(() => {
    if (!milestones?.length) return null;

    const visitaAObra = milestones.find(m => m?.id === 5);

    if (visitaAObra) {
      return visitaAObra.status === 2;
    }

    return false;
  }, [milestones]);

  const loadOnboarding = React.useCallback(async () => {
    try {
      const onboardingResponse = await getOnboardingInformation();
      if (onboardingResponse.status !== 204) {
        setShowOnboarding(onboardingResponse.data.onboarding);
        setShowIndicacao(onboardingResponse.data.indicacao);
      }
      setLoadedOnboarding(true);
    } catch (error) {
      const newCount = countTryOnboarding + 1;
      setCountTryOnboarding(newCount);
      if (newCount === TRY_RETRIES_COUNT_TOTAL) {
        setShowOnboarding(false);
      }
      trackException(error);
      trackTraceException("loadOnboarding", error);
    }
  }, [countTryOnboarding]);

  useEffect(() => {
    if (
      countTryOnboarding < TRY_RETRIES_COUNT_TOTAL &&
      !loading &&
      !loadedOnboarding
    ) {
      loadOnboarding();
    }
  }, [
    countTryOnboarding,
    setCountTryOnboarding,
    loading,
    loadedOnboarding,
    loadOnboarding
  ]);

  const value = React.useMemo(
    () => ({
      loading,
      setLoading,
      loadOnboarding,
      showOnboarding,
      loadedOnboarding,
      showIndicacao,
      milestones,
      setMilestones,
      milestonesError,
      setMilestonesError,
      isVisitaAObraConcluido,
      isVAConcluida,
      isECConcluido
    }),
    [
      loading,
      loadOnboarding,
      showOnboarding,
      loadedOnboarding,
      showIndicacao,
      milestones,
      setMilestones,
      milestonesError,
      setMilestonesError,
      isVisitaAObraConcluido,
      isVAConcluida,
      isECConcluido
    ]
  );

  return <HomeContext.Provider value={value}>{children}</HomeContext.Provider>;
};

HomeContextProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default HomeContext;
